import axios from 'axios';
import { CYLINDO_ACCOUNT_ID } from 'commons/constants';
import { logError } from 'commons/logger';
import { CYLINDO_CONTENT_API } from './defaults';

export const EMPTY_ENTRY = Object.freeze({
  config: null,
  version: null,
  loading: false,
});

const cylindoConfigPromises = {};
const cylindoStoreEntries = {};
const cylindoStoreListeners = new Set();

// Cylindo caches the unversioned config for a year, at the CDN and in the browser, so a
// plain read could outlive a publish (CON-8451). The CDN keys on the full URL, so a
// per-session query param forces a miss and the origin returns the current published
// config. Pinning `?version=` instead would not help: a stale copy also carries a stale
// version number.
const SESSION_CACHE_KEY = Date.now();

const fetchCylindoConfig = productId => {
  if (!cylindoConfigPromises[productId]) {
    cylindoConfigPromises[productId] = axios.get(
      `${CYLINDO_CONTENT_API}/${CYLINDO_ACCOUNT_ID}/products/${productId}/configuration?t=${SESSION_CACHE_KEY}`
    );
  }
  return cylindoConfigPromises[productId];
};

const notifyCylindoStore = () => {
  cylindoStoreListeners.forEach(fn => fn());
};

export const subscribeToCylindoStore = listener => {
  cylindoStoreListeners.add(listener);
  return () => {
    cylindoStoreListeners.delete(listener);
  };
};

export const getCylindoEntry = productId =>
  productId ? cylindoStoreEntries[productId] || EMPTY_ENTRY : EMPTY_ENTRY;

export const requestCylindoFetch = productId => {
  if (!productId || cylindoStoreEntries[productId]) return;

  cylindoStoreEntries[productId] = Object.freeze({
    config: null,
    version: null,
    loading: true,
  });
  notifyCylindoStore();

  fetchCylindoConfig(productId)
    .then(res => {
      if (!res.data) {
        cylindoStoreEntries[productId] = EMPTY_ENTRY;
        notifyCylindoStore();
        return null;
      }
      // Stick to the published config, no more probing for a higher version. Cylindo
      // serves unpublished drafts one version up, and those can carry option names that
      // no longer match our SKUs, so nothing resolves and the images break (CON-8732).
      cylindoStoreEntries[productId] = Object.freeze({
        config: res.data,
        version: res.data.version ?? null,
        loading: false,
      });
      notifyCylindoStore();
      return null;
    })
    .catch(e => {
      logError(e);
      cylindoStoreEntries[productId] = EMPTY_ENTRY;
      notifyCylindoStore();
    });
};
